import * as Sentry from '@sentry/core';

import { extractHTTPError } from '../errors';
import { getSentryCurrentScope, SENTRY_SPAN_ATTRIBUTE_SAMPLE_RATE } from '../sentry';
import { GenericTrace, type TraceOptions } from './generic';

/**
 * Tracing implementation with binding with the Sentry SDK.
 */
export function SentryTrace(TraceImpl: typeof GenericTrace = GenericTrace) {
    return class SentryTrace extends TraceImpl {
        private sentrySpan: Sentry.Span | null = null;
        private traceSampleRate?: number;

        constructor(name: string, opts: TraceOptions = {}) {
            super(name, opts);

            this.traceSampleRate =
                typeof opts.sampleRate === 'number' ? opts.sampleRate : undefined;
        }

        public async wrap<T>(fn: () => Promise<T>): Promise<T> {
            if (process.env.GITBOOK_NO_ANALYTICS) {
                return super.wrap(fn);
            }

            const attributes: Sentry.SpanAttributes = {
                ...(this.traceSampleRate
                    ? { [SENTRY_SPAN_ATTRIBUTE_SAMPLE_RATE]: this.traceSampleRate }
                    : {}),
            };

            return Sentry.startSpanManual(
                {
                    name: this.name,
                    forceTransaction: true,
                    attributes,
                },
                async (span) => {
                    this.sentrySpan = span;
                    const scope = getSentryCurrentScope();
                    const previousContext = scope.getPropagationContext();
                    scope.setPropagationContext({
                        ...previousContext,
                        traceId: this.traceId,
                        parentSpanId: this.parentSpanId ?? undefined,
                        propagationSpanId: this.spanId,
                    });

                    try {
                        return await super.wrap(fn);
                    } finally {
                        scope.setPropagationContext(previousContext);
                    }
                }
            );
        }

        protected end(error: Error | null) {
            super.end(error);

            if (process.env.GITBOOK_NO_ANALYTICS) {
                return;
            }

            const code = error ? extractHTTPError(error).statusCode : 200;
            const span = this.sentrySpan;
            if (span) {
                const status = Sentry.getSpanStatusFromHttpCode(code);
                span.setStatus(status).end();
                this.sentrySpan = null;
            }

            Sentry.addBreadcrumb({
                category: this.name,
                level: 'info',
                message: `${error ? 'failed' : 'succeeded'} in ${
                    this.endTime! - this.startTime!
                }ms`,
                data: this.labels,
            });
        }
    };
}
